import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { t, locale } from '../i18n';
import { SectionTitle, Input } from '../ui';

const MAPTILER_KEY = 'PLOBYZLBxw9drUpSeCic';
const STYLE_URL = `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}`;
const GEOCODE_URL = 'https://api.maptiler.com/geocoding';

/** Distance approximative entre deux points (formule de Haversine), en mètres. */
function distanceMeters(a, b) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}

function formatDistance(m) {
    return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`;
}

/**
 * Extrait les composants d'adresse d'un feature de géocodage MapTiler
 * (utilisé pour l'auto-remplissage des champs route / ville / code postal).
 */
export function parseAddress(feature) {
    if (!feature) return null;
    const ctx = feature.context || [];
    const pick = (prefix) => {
        const c = ctx.find((x) => String(x.id || '').startsWith(prefix));
        return c ? c.text : '';
    };
    const route = [feature.address, feature.text].filter(Boolean).join(' ');
    return {
        route,
        code_postal: pick('postal_code'),
        ville: pick('municipality') || pick('place'),
        pays: pick('country'),
    };
}

function geocodeForward(query) {
    const url = `${GEOCODE_URL}/${encodeURIComponent(query)}.json`
        + `?key=${MAPTILER_KEY}&language=${locale}&autocomplete=true&limit=5`;
    return fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(r))).then((d) => d.features || []);
}

function geocodeReverse(lng, lat) {
    const url = `${GEOCODE_URL}/${lng},${lat}.json`
        + `?key=${MAPTILER_KEY}&language=${locale}&types=address`;
    return fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((d) => (d.features || [])[0] || null);
}

/**
 * Sélecteur de localisation sur carte MapLibre.
 *
 * - Champ de recherche d'adresse (autocomplétion via le géocodage MapTiler) :
 *   sélectionner un résultat centre la carte, pose le marqueur et remonte les
 *   composants d'adresse au parent (auto-remplissage).
 * - Marqueur déplaçable (drag) + clic sur la carte : re-géocode l'adresse.
 * - L'emplacement d'origine reste affiché en repère gris avec la distance.
 *
 * Composant contrôlé « léger » : il gère la carte en interne et remonte les
 * changements au parent via onChange (coordonnées) et onAddressResolved
 * (adresse). L'enregistrement est assuré par le bouton unique du formulaire.
 */
export default function LocationEditor({ place, onChange, onAddressResolved }) {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const marker = useRef(null);
    const debounce = useRef(null);
    // Refs de callbacks pour éviter les closures périmées dans les handlers MapLibre.
    const cb = useRef({});
    cb.current = { onChange, onAddressResolved };

    const origin = { lat: place.latitude, lng: place.longitude };
    const hasOrigin = Number.isFinite(origin.lat) && Number.isFinite(origin.lng);

    const [pos, setPos] = useState(hasOrigin ? origin : { lat: 46.6, lng: 2.5 });
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);

    // Applique une nouvelle position : marqueur + parent, puis géocodage inverse.
    const applyPosition = (next, feature) => {
        setPos(next);
        marker.current?.setLngLat([next.lng, next.lat]);
        cb.current.onChange?.(next);
        if (feature) {
            const addr = parseAddress(feature);
            if (addr) cb.current.onAddressResolved?.(addr);
        } else {
            geocodeReverse(next.lng, next.lat)
                .then((f) => { const a = parseAddress(f); if (a) cb.current.onAddressResolved?.(a); })
                .catch(() => { /* silencieux : l'utilisateur peut saisir à la main */ });
        }
    };

    // Init carte (une seule fois)
    useEffect(() => {
        if (map.current || !mapContainer.current) return undefined;

        map.current = new maplibregl.Map({
            container: mapContainer.current,
            style: STYLE_URL,
            center: [pos.lng, pos.lat],
            zoom: hasOrigin ? 14 : 5,
        });
        map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
        map.current.addControl(new maplibregl.GeolocateControl({ trackUserLocation: false }), 'top-right');

        if (hasOrigin) {
            new maplibregl.Marker({ color: '#97A0B8' })
                .setLngLat([origin.lng, origin.lat])
                .setPopup(new maplibregl.Popup({ closeButton: false }).setText(t('loc_origine')))
                .addTo(map.current);
        }

        marker.current = new maplibregl.Marker({ color: '#165DD7', draggable: true })
            .setLngLat([pos.lng, pos.lat])
            .addTo(map.current);

        marker.current.on('dragend', () => {
            const ll = marker.current.getLngLat();
            applyPosition({ lat: ll.lat, lng: ll.lng });
        });

        map.current.on('click', (e) => {
            applyPosition({ lat: e.lngLat.lat, lng: e.lngLat.lng });
        });

        return () => { map.current?.remove(); map.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recherche d'adresse (debounce 300 ms, min. 3 caractères)
    const onQueryChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        if (debounce.current) clearTimeout(debounce.current);
        if (value.trim().length < 3) { setResults([]); return; }
        debounce.current = setTimeout(() => {
            geocodeForward(value.trim())
                .then((features) => setResults(features))
                .catch(() => setResults([]));
        }, 300);
    };

    const selectResult = (feature) => {
        const [lng, lat] = feature.center;
        setQuery(feature.place_name || '');
        setResults([]);
        map.current?.flyTo({ center: [lng, lat], zoom: 16 });
        applyPosition({ lat, lng }, feature);
    };

    const moved = hasOrigin ? distanceMeters(origin, pos) : null;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                <SectionTitle>{t('loc_titre')}</SectionTitle>
                {moved !== null && moved > 1 && (
                    <span className="text-muted text-sm">{t('loc_deplacement')} : {formatDistance(moved)}</span>
                )}
            </div>

            <p className="text-muted text-sm" style={{ marginTop: 0 }}>{t('loc_aide')}</p>

            {/* Recherche d'adresse */}
            <div className="geo-search">
                <Input
                    placeholder={t('loc_search')}
                    value={query}
                    onChange={onQueryChange}
                    autoComplete="off"
                />
                {results.length > 0 && (
                    <div className="geo-search__results">
                        {results.map((feature) => (
                            <button
                                key={feature.id}
                                type="button"
                                className="geo-search__item"
                                onClick={() => selectResult(feature)}
                            >
                                {feature.place_name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div ref={mapContainer} className="location-map" />

            <div className="toolbar" style={{ marginTop: 8 }}>
                <span className="text-muted text-sm">{pos.lat.toFixed(6)}, {pos.lng.toFixed(6)}</span>
            </div>
        </div>
    );
}
